import {
  VotingDelegation,
  DelegationSummary,
  CreateDelegationDto,
  VotingWeightInfo,
  DelegationStatus,
  DelegationOperatorAssembliesResponse,
  ManagedAssemblyDelegationsResponse,
} from '../types/delegation';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class DelegationService {
  private normalizeEmail(email: string): string {
    return email.trim().toLowerCase();
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Crea una delegación (sin autenticación, desde email)
   */
  async createDelegation(data: CreateDelegationDto): Promise<VotingDelegation> {
    const payload: CreateDelegationDto = {
      ...data,
      delegateEmail: this.normalizeEmail(data.delegateEmail),
    };

    const response = await fetch(`${API_BASE_URL}/delegations/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const result = await this.handleResponse<{ success: boolean; data: VotingDelegation }>(response);
    return result.data;
  }

  /**
   * Crea una delegación desde la app autenticada
   */
  async createDelegationAuthenticated(assemblyId: string, delegateEmail: string): Promise<VotingDelegation> {
    const response = await fetch(`${API_BASE_URL}/delegations/create-auth/${assemblyId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ delegateEmail: this.normalizeEmail(delegateEmail) }),
    });

    const result = await this.handleResponse<{ success: boolean; data: VotingDelegation }>(response);
    return result.data;
  }

  /**
   * Acepta una delegación y descarga automáticamente el PDF del poder
   */
  async acceptDelegation(delegationId: string, token: string): Promise<VotingDelegation> {
    const response = await fetch(`${API_BASE_URL}/delegations/${delegationId}/accept?token=${token}`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error al aceptar la delegación' }));
      throw new Error(errorData.message || 'Error al aceptar la delegación');
    }

    const contentType = response.headers.get('Content-Type') || '';

    if (contentType.includes('application/pdf')) {
      // El backend devuelve el PDF: descargar automáticamente
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `poder-delegacion-${delegationId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else {
      // El backend responde JSON indicando que no hay PDF (plantilla no disponible)
      await response.json().catch(() => ({}));
    }

    // Retornar datos de la delegación (mínimos ya que el PDF no incluye JSON)
    // El frontend solo necesita saber que fue exitoso
    return {
      id: delegationId,
      status: DelegationStatus.ACCEPTED,
      acceptedAt: new Date().toISOString(),
      assemblyId: '',
      delegatorId: '',
      delegateId: '',
      delegatedCoefficient: 0,
      createdAt: new Date().toISOString(),
    } as VotingDelegation;
  }

  /**
   * Rechaza una delegación
   */
  async rejectDelegation(delegationId: string, token: string): Promise<VotingDelegation> {
    const response = await fetch(`${API_BASE_URL}/delegations/${delegationId}/reject?token=${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    const result = await this.handleResponse<{ success: boolean; data: VotingDelegation }>(response);
    return result.data;
  }

  /**
   * Revoca una delegación (requiere autenticación)
   */
  async revokeDelegation(delegationId: string): Promise<VotingDelegation> {
    const response = await fetch(`${API_BASE_URL}/delegations/${delegationId}/revoke`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: VotingDelegation }>(response);
    return result.data;
  }

  /**
   * Obtiene las delegaciones del usuario para una asamblea
   */
  async getMyDelegations(assemblyId: string): Promise<DelegationSummary> {
    const response = await fetch(`${API_BASE_URL}/delegations/my/${assemblyId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: DelegationSummary }>(response);
    return result.data;
  }

  /**
   * Calcula el peso de voto del usuario
   */
  async getVotingWeight(assemblyId: string): Promise<VotingWeightInfo> {
    const response = await fetch(`${API_BASE_URL}/delegations/voting-weight/${assemblyId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: VotingWeightInfo }>(response);
    return result.data;
  }

  /**
   * Genera un token de delegación para el usuario actual
   */
  async generateDelegationToken(assemblyId: string): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/delegations/generate-token/${assemblyId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: { token: string } }>(response);
    return result.data.token;
  }

  async getManagerAssemblies(complexId?: string): Promise<DelegationOperatorAssembliesResponse> {
    const query = complexId ? `?complexId=${encodeURIComponent(complexId)}` : '';
    const response = await fetch(`${API_BASE_URL}/delegations/manager/assemblies${query}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{
      success: boolean;
      data: DelegationOperatorAssembliesResponse;
    }>(response);
    return result.data;
  }

  async getManagerAssemblyDelegations(
    assemblyId: string,
    options?: { status?: DelegationStatus; complexId?: string }
  ): Promise<ManagedAssemblyDelegationsResponse> {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.complexId) params.set('complexId', options.complexId);
    const query = params.toString() ? `?${params.toString()}` : '';

    const response = await fetch(
      `${API_BASE_URL}/delegations/manager/assemblies/${assemblyId}/delegations${query}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );
    const result = await this.handleResponse<{
      success: boolean;
      data: ManagedAssemblyDelegationsResponse;
    }>(response);
    return result.data;
  }

  async downloadPowerOfAttorney(delegationId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/delegations/${delegationId}/power`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error al descargar el poder' }));
      throw new Error(errorData.message || 'Error al descargar el poder');
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || `poder-delegacion-${delegationId}.pdf`;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async revokeDelegationAsManager(delegationId: string): Promise<VotingDelegation> {
    const response = await fetch(`${API_BASE_URL}/delegations/manager/delegations/${delegationId}/revoke`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: VotingDelegation }>(response);
    return result.data;
  }
}

export const delegationService = new DelegationService();
